import { Document, DocumentVersion } from "@models";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useGetDevice, useGetFile, useGetUser } from "src/hooks/queries";
import { buildDownloadFileName } from "src/utils";
import { getOrgFromUser } from "src/utils/user";
import { handleFileDownload } from "src/components/DocViewer/components/helpers/fileDownload";
import DiffViewer from "src/components/DocViewer/components/markdownDocViewer/diffViewer/DiffViewer";
import MarkdownReader from "src/components/DocViewer/components/markdownDocViewer/diffViewer/MarkdownReader";
import { FilePreview } from "src/components/DocViewer/components/fileDocViewer/FilePreview";
import { getDocVersionRevisionNumber } from "src/utils/documents";

type ViewerTab = "document" | "changes";

interface ReadOnlyQmsDocumentViewerProps {
  orgId: string;
  document: Document;
  selectedVersion: DocumentVersion;
  previousVersion?: DocumentVersion | null;
}

const getMarkdown = (version?: DocumentVersion | null) =>
  version?.snapshot ?? version?.contentTemplate ?? "";

export const ReadOnlyQmsDocumentViewer = ({
  orgId,
  document,
  selectedVersion,
  previousVersion,
}: ReadOnlyQmsDocumentViewerProps) => {
  const [activeTab, setActiveTab] = useState<ViewerTab>("document");

  useEffect(() => {
    setActiveTab("document");
  }, [selectedVersion.id]);

  const currentRevision = getDocVersionRevisionNumber(
    selectedVersion,
    document.versions
  );
  const previousRevision = previousVersion
    ? getDocVersionRevisionNumber(previousVersion, document.versions)
    : null;
  const changesLabel =
    previousRevision != null
      ? `Changes (v${previousRevision} -> v${currentRevision})`
      : `Changes (v${currentRevision})`;

  if (document.userUpload) {
    return (
      <ReadOnlyUploadedDocument
        activeTab={activeTab}
        document={document}
        onTabChange={setActiveTab}
        changesLabel={changesLabel}
        orgId={orgId}
        selectedVersion={selectedVersion}
      />
    );
  }

  return (
    <ReadOnlyManagedDocument
      activeTab={activeTab}
      onTabChange={setActiveTab}
      changesLabel={changesLabel}
      currentMarkdown={getMarkdown(selectedVersion)}
      previousMarkdown={getMarkdown(previousVersion)}
    />
  );
};

const ReadOnlyManagedDocument = ({
  activeTab,
  onTabChange,
  changesLabel,
  currentMarkdown,
  previousMarkdown,
}: {
  activeTab: ViewerTab;
  onTabChange: (tab: ViewerTab) => void;
  changesLabel: string;
  currentMarkdown: string;
  previousMarkdown: string;
}) => {
  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
      <ViewerTabs
        activeTab={activeTab}
        onTabChange={onTabChange}
        changesLabel={changesLabel}
        showChangesTab
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 3 }}>
        {activeTab === "changes" ? (
          <DiffViewer
            currentMarkdown={currentMarkdown}
            previousMarkdown={previousMarkdown}
          />
        ) : (
          <MarkdownReader markdown={currentMarkdown} />
        )}
      </Box>
    </Box>
  );
};

const ReadOnlyUploadedDocument = ({
  activeTab,
  document,
  onTabChange,
  changesLabel,
  orgId,
  selectedVersion,
}: {
  activeTab: ViewerTab;
  document: Document;
  onTabChange: (tab: ViewerTab) => void;
  changesLabel: string;
  orgId: string;
  selectedVersion: DocumentVersion;
}) => {
  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({
    orgId,
    deviceId: document.deviceId ?? "",
  });
  const hasChangesFile = Boolean(selectedVersion.changesFileId);
  const changesFileId = selectedVersion.changesFileId ?? "";
  const documentFileId = selectedVersion.fileId ?? "";
  const showChanges = activeTab === "changes" && hasChangesFile;
  const activeFile = showChanges
    ? selectedVersion.changesFile
    : selectedVersion.file;
  const activeFileId = showChanges ? changesFileId : documentFileId;

  const { data: documentFileBlob, isLoading: isDocumentFileLoading } =
    useGetFile({
      orgId,
      fileId: documentFileId,
    });
  const { data: changesFileBlob, isLoading: isChangesFileLoading } =
    useGetFile({
      orgId,
      fileId: showChanges ? changesFileId : "",
    });

  const activeBlob = showChanges
    ? changesFileBlob?.data
    : documentFileBlob?.data;

  const isLoading = showChanges ? isChangesFileLoading : isDocumentFileLoading;

  const handleDownload = () => {
    if (!activeBlob || !activeFile) return;
    handleFileDownload(
      activeBlob,
      buildDownloadFileName({
        baseName: activeFile.filename ?? "training-document",
        revision: selectedVersion.revision,
        deviceName: document.deviceId ? device?.name : null,
        organizationName: getOrgFromUser(user, orgId)?.name,
      })
    );
  };

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
      <ViewerTabs
        activeTab={activeTab}
        onTabChange={onTabChange}
        changesLabel={changesLabel}
        showChangesTab={hasChangesFile}
      />
      <Box
        sx={{
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1,
          px: 3,
          py: 1.5,
        }}
      >
        <Box sx={{ color: "text.secondary", flex: 1, fontSize: 13, minWidth: 0 }}>
          {activeFile?.filename ?? "No file available"}
        </Box>
        <Button
          disabled={!activeBlob || !activeFile}
          onClick={handleDownload}
          size="small"
          variant="outlined"
        >
          Download file
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (!activeFileId || !activeFile || !activeBlob) && (
          <Alert severity="error" sx={{ m: 3 }}>
            Could not load the uploaded training document.
          </Alert>
        )}
        {!isLoading && activeFile && activeBlob && (
          <FilePreview
            file={activeFile}
            fileBlob={activeBlob}
            noPreviewMessage="Download this file to review it before acknowledging the training."
          />
        )}
      </Box>
    </Box>
  );
};

const ViewerTabs = ({
  activeTab,
  onTabChange,
  changesLabel,
  showChangesTab,
}: {
  activeTab: ViewerTab;
  onTabChange: (tab: ViewerTab) => void;
  changesLabel: string;
  showChangesTab: boolean;
}) => (
  <Tabs
    value={showChangesTab ? activeTab : "document"}
    onChange={(_, value: ViewerTab) => onTabChange(value)}
    sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
  >
    <Tab value="document" label="Document" />
    {showChangesTab && <Tab value="changes" label={changesLabel} />}
  </Tabs>
);
